/**
 * Data Normalization Utilities
 * 
 * This module provides functions to normalize data from different sources
 * (API vs localStorage) to ensure consistent field naming across the application.
 */

/**
 * Normalizes asset object to use consistent camelCase field names
 * Supports both API (snake_case) and legacy (camelCase) formats
 * 
 * @param {Object} asset - Raw asset object from any source
 * @returns {Object} Normalized asset object with camelCase fields
 */
export const normalizeAsset = (asset) => {
  if (!asset) return {};
  
  const normalized = { ...asset };
  
  // PM Due Date normalization
  if (normalized.pm_due && !normalized.pmDue) {
    normalized.pmDue = normalized.pm_due;
  }
  
  // Calibration Due Date normalization
  if (normalized.cal_due && !normalized.calDue) {
    normalized.calDue = normalized.cal_due;
  }
  
  // Asset ID normalization
  if (normalized.asset_id && !normalized.assetId) {
    normalized.assetId = normalized.asset_id;
  }
  
  // Serial Number normalization
  if (normalized.serial_no && !normalized.serialNo) {
    normalized.serialNo = normalized.serial_no;
  }
  
  // Equipment Name normalization
  if (normalized.equipment_name && !normalized.equipmentName) {
    normalized.equipmentName = normalized.equipment_name;
  }
  
  // Installation Date normalization
  if (normalized.installation_date && !normalized.installationDate) {
    normalized.installationDate = normalized.installation_date;
  }
  
  // PM Done Date normalization
  if (normalized.pm_done && !normalized.pmDone) {
    normalized.pmDone= normalized.pm_done;
  }
  
  // Calibration Done Date normalization
  if (normalized.calibration_done && !normalized.calibrationDone) {
    normalized.calibrationDone= normalized.calibration_done;
  }
  
  // Warranty Expiry normalization
  if (normalized.warranty_expiry && !normalized.warrantyExpiry) {
    normalized.warrantyExpiry = normalized.warranty_expiry;
  }
  
  // AMC/CMC Contract normalization
  if (normalized.amc_cmc_contract && !normalized.amcCmcContract) {
    normalized.amcCmcContract = normalized.amc_cmc_contract;
  }
  
 return normalized;
};

/**
 * Normalizes an array of assets
 * @param {Array} assets - Array of raw asset objects
 * @returns {Array} Array of normalized asset objects
 */
export const normalizeAssets = (assets) => {
  if (!Array.isArray(assets)) {
   console.warn('normalizeAssets: Expected array, got', typeof assets);
    return [];
  }
  
 return assets.map(asset => normalizeAsset(asset));
};

/**
 * Normalizes call log object to use consistent camelCase field names
 * 
 * @param {Object} log- Raw call log object from any source
 * @returns {Object} Normalized call log object with camelCase fields
 */
export const normalizeCallLog = (log) => {
  if (!log) return {};
  
  const normalized = { ...log };
  
  // Nature of Complaint normalization
  if (normalized.nature_of_complaint && !normalized.natureOfComplaint) {
    normalized.natureOfComplaint = normalized.nature_of_complaint;
  }
  
  // Action Taken normalization
  if (normalized.action_taken && !normalized.actionTaken) {
    normalized.actionTaken = normalized.action_taken;
  }
  
  // Start Time normalization
  if (normalized.start_time && !normalized.startTime) {
    normalized.startTime = normalized.start_time;
  }
  
  // End Time normalization
  if (normalized.end_time && !normalized.endTime) {
    normalized.endTime = normalized.end_time;
  }
  
  // Call Attend Time normalization
  if (normalized.call_attend_time && !normalized.callAttendTime) {
    normalized.callAttendTime = normalized.call_attend_time;
  }
  
  // Response Time normalization
  if (normalized.response_time && !normalized.responseTime) {
    normalized.responseTime = normalized.response_time;
  }
  
  // Resolution Time normalization
  if (normalized.resolution_time && !normalized.resolutionTime) {
    normalized.resolutionTime = normalized.resolution_time;
  }
  
  // Attended By normalization
  if (normalized.attended_by && !normalized.attendedBy) {
    normalized.attendedBy = normalized.attended_by;
  }
  
  // Call End Date normalization
  if (normalized.call_end_date && !normalized.callEndDate) {
    normalized.callEndDate = normalized.call_end_date;
  }
  
  // Spares Used normalization
  if (normalized.spares_used && !normalized.sparesUsed) {
    normalized.sparesUsed = normalized.spares_used;
  }
  
  // Spares Cost normalization
  if (normalized.spares_cost && !normalized.sparesCost) {
    normalized.sparesCost = normalized.spares_cost;
  }
  
  // Call Status normalization
  if (normalized.call_status && !normalized.callStatus) {
    normalized.callStatus = normalized.call_status;
  }
  
  // User Name normalization
  if (normalized.user_name && !normalized.userName) {
    normalized.userName = normalized.user_name;
  }
  
  // User Role normalization
  if (normalized.user_role && !normalized.userRole) {
    normalized.userRole = normalized.user_role;
  }
  
  // Created At normalization
  if (normalized.created_at && !normalized.createdAt) {
    normalized.createdAt = normalized.created_at;
  }
  
  // Savings After Spares Cost normalization
  if (normalized.savings_after_spares_cost && !normalized.savingsAfterSparesCost) {
    normalized.savingsAfterSparesCost = normalized.savings_after_spares_cost;
  }
  
 return normalized;
};

/**
 * Normalizes an array of call logs
 * @param {Array} logs - Array of raw call log objects
 * @returns {Array} Array of normalized call log objects
 */
export const normalizeCallLogs = (logs) => {
  if (!Array.isArray(logs)) {
   console.warn('normalizeCallLogs: Expected array, got', typeof logs);
    return [];
  }
  
 return logs.map(log => normalizeCallLog(log));
};

/**
 * Auto-detect field naming convention in a dataset
 * @param {Array} items - Array of objects to analyze
 * @returns {Object} Detection results with counts and detected convention
 */
export const detectFieldConvention = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      snakeCase: 0,
      camelCase: 0,
      detected: 'unknown',
     confidence: 0
    };
  }
  
  let snakeCase = 0;
  let camelCase = 0;
  
  // Check first item (assuming consistency within dataset)
  const sample = items[0];
  
  // Check for snake_case fields
  if (sample.pm_due) snakeCase++;
  if (sample.cal_due) snakeCase++;
  if (sample.asset_id) snakeCase++;
  if (sample.nature_of_complaint) snakeCase++;
  if (sample.action_taken) snakeCase++;
  
  // Check for camelCase fields
  if (sample.pmDue) camelCase++;
  if (sample.calDue) camelCase++;
  if (sample.assetId) camelCase++;
  if (sample.natureOfComplaint) camelCase++;
  if (sample.actionTaken) camelCase++;
  
  const total = snakeCase + camelCase;
  const detected = snakeCase > camelCase ? 'snake_case' : 'camelCase';
  const confidence = total > 0 ? Math.max(snakeCase, camelCase) / total : 0;
  
 return {
    snakeCase,
    camelCase,
    detected,
   confidence
  };
};

/**
 * Smart normalize - automatically normalizes data based on detected convention
 * @param {Array} items - Array of objects to normalize
 * @param {String} type - Type of data ('assets' or 'callLogs')
 * @returns {Array} Normalized array
 */
export const smartNormalize = (items, type) => {
  if (!Array.isArray(items)) {
   console.warn('smartNormalize: Expected array, got', typeof items);
    return [];
  }
  
  const convention = detectFieldConvention(items);
  console.log(`📊 Detected ${convention.detected} naming convention (${Math.round(convention.confidence * 100)}% confidence)`);
  
  if (type === 'assets') {
    return normalizeAssets(items);
  } else if (type === 'callLogs') {
    return normalizeCallLogs(items);
  }
  
 return items;
};
